import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Divider,
  Link,
} from '@mui/material';

const Section = ({ id, title, children }) => (
  <Box id={id} sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicy = () => {
  return (
    <>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', pt: { xs: 8, md: 10 }, pb: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="md">
          <Typography component="h1" variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last Updated: 01 August 2026
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
        {/* Intro */}
        <Typography variant="body1" paragraph>
          This Privacy Policy ("<strong>Policy</strong>") describes how <strong>Creativecrewz</strong>, a company incorporated under the laws of India having its registered office at <strong>6-3-240/6/1, 1st Floor, Sarada Street, Road D-block, Maheswari Towers, Prem Nagar Road adj. Lane, Banjara Hills, Hyderabad, Telangana 500004, India</strong> ("<strong>Company</strong>", "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>"), collects, uses, stores, processes, discloses, and protects information collected through <strong>InfHire (www.infhire.com)</strong> (the "<strong>Platform</strong>"), an influencer marketing platform that connects brands/advertisers ("<strong>Brands</strong>") with content creators/influencers ("<strong>Influencers</strong>"), together referred to as "<strong>Users</strong>", "<strong>you</strong>".
        </Typography>
        <Typography variant="body1" paragraph>
          This Policy is published in accordance with the Digital Personal Data Protection Act, 2023 ("<strong>DPDP Act</strong>"), the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, as amended from time to time.
        </Typography>
        <Typography variant="body1" paragraph>
          By accessing or using the Platform, you consent to the collection, use, storage, and disclosure of your information as described in this Policy. If you do not agree, please do not use the Platform.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Section id="definitions" title="1. Definitions">
          <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
            <li><strong>"Personal Data"</strong> means any data about an individual who is identifiable by or in relation to such data.</li>
            <li><strong>"Sensitive Personal Data"</strong> includes financial information (bank account/UPI/payment details), government ID numbers (PAN, Aadhaar last 4 digits where applicable), passwords, and any data classified as sensitive under applicable Indian law.</li>
            <li><strong>"Data Principal"</strong> means the individual to whom the Personal Data relates (i.e., you).</li>
            <li><strong>"Data Fiduciary"</strong> means the Company, which determines the purpose and means of processing your Personal Data.</li>
            <li><strong>"Processing"</strong> means any operation performed on Personal Data, including collection, storage, use, sharing, or deletion.</li>
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="information-we-collect" title="2. Information We Collect">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>2.1 Information You Provide Directly</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Name, email address, mobile number, date of birth, gender</li>
            <li>Profile details (bio, niche/category, portfolio, social media handles/links)</li>
            <li>KYC and payment information (PAN, GSTIN, bank account/UPI ID, billing address) for payouts/invoicing</li>
            <li>Brand information (company name, GSTIN, billing details) for Brand accounts</li>
            <li>Content submitted for campaigns (images, videos, captions, drafts)</li>
            <li>Communications with us or through in-platform messaging with Brands/Influencers</li>
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>2.2 Information Collected Automatically</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Device information (IP address, browser type, operating system, device identifiers)</li>
            <li>Usage data (pages visited, clicks, session duration, referral URLs)</li>
            <li>Cookies and similar tracking technologies (see Section 8)</li>
            <li>Approximate location derived from IP address</li>
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>2.3 Information from Third Parties</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
            <li>Social media platform data obtained via API integrations (e.g., Instagram, YouTube, Facebook) that you authorize us to access, including follower counts, engagement rates, and public post analytics, for verification and campaign performance reporting.</li>
            <li>Information from payment gateways/processors regarding transaction status (we do not store full card details).</li>
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="purpose" title="3. Purpose of Collection and Legal Basis for Processing">
          <Typography variant="body1" paragraph>
            We process your Personal Data for the following purposes, based on your <strong>consent</strong>, <strong>contractual necessity</strong>, or <strong>legitimate use</strong> as permitted under the DPDP Act:
          </Typography>
          <Typography variant="body1" component="ol" sx={{ pl: 2 }}>
            <li>Creating and managing your account and profile</li>
            <li>Matching Influencers with relevant Brand campaigns and vice versa</li>
            <li>Facilitating campaign briefs, content approvals, and collaboration workflows</li>
            <li>Processing payments, invoicing, and TDS/GST compliance</li>
            <li>Verifying identity and preventing fraud, fake followers, or bot activity</li>
            <li>Communicating campaign updates, offers, and service notifications</li>
            <li>Improving Platform features, analytics, and user experience</li>
            <li>Complying with legal obligations, including those under the IT Act, Income Tax Act, GST law, and orders of courts/government authorities</li>
            <li>Enforcing our{' '}
              <Link component={RouterLink} to="/terms" color="primary">Terms and Conditions</Link>
              {' '}and resolving disputes between Users
            </li>
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            We do not process Sensitive Personal Data without your explicit consent, except where required by law.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="sharing" title="4. Sharing and Disclosure of Information">
          <Typography variant="body1" paragraph>We may share your information with:</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
            <li><strong>Brands/Advertisers:</strong> Influencer profile, portfolio, engagement metrics, and submitted content, strictly for campaign evaluation and execution.</li>
            <li><strong>Influencers:</strong> Campaign briefs, deliverables, and Brand contact details as necessary to execute a collaboration.</li>
            <li><strong>Payment Processors/Banks:</strong> To process payouts and refunds.</li>
            <li><strong>Service Providers:</strong> Cloud hosting, analytics, customer support, and email/SMS providers, bound by confidentiality obligations.</li>
            <li><strong>Legal and Regulatory Authorities:</strong> Where required under law, court order, or government direction, including under Section 69 or 69A of the IT Act.</li>
            <li><strong>Business Transfers:</strong> In case of a merger, acquisition, or sale of assets, subject to equivalent privacy protections for the acquiring entity.</li>
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            We do <strong>not</strong> sell your Personal Data to third parties for their independent marketing purposes.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="data-retention" title="5. Data Retention">
          <Typography variant="body1">
            We retain Personal Data only for as long as necessary to fulfil the purposes described in this Policy, or as required under applicable law (e.g., financial records under the Income Tax Act/Companies Act for the statutorily prescribed period). Upon account deletion, we will delete or anonymize Personal Data within <strong>90 days</strong>, except data we are legally required to retain.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="your-rights" title="6. Your Rights as a Data Principal (under the DPDP Act, 2023)">
          <Typography variant="body1" paragraph>Subject to applicable law, you have the right to:</Typography>
          <Typography variant="body1" component="ol" sx={{ pl: 2 }}>
            <li><strong>Access</strong> a summary of your Personal Data and processing activities</li>
            <li><strong>Correction and Updating</strong> of inaccurate or incomplete Personal Data</li>
            <li><strong>Erasure</strong> of Personal Data that is no longer necessary for the purpose collected</li>
            <li><strong>Withdraw Consent</strong> at any time, without affecting the lawfulness of processing before withdrawal</li>
            <li><strong>Grievance Redressal</strong> through our Grievance Officer (Section 10)</li>
            <li><strong>Nominate</strong> an individual to exercise these rights on your behalf in case of death or incapacity</li>
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            To exercise these rights, write to us at{' '}
            <Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link>.
            {' '}We will respond within the timeline prescribed under applicable law.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="data-security" title="7. Data Security">
          <Typography variant="body1">
            We implement reasonable security practices and procedures under Section 43A of the IT Act and applicable rules, including encryption of sensitive data in transit, access controls, firewalls, and periodic security audits. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="cookies" title="8. Cookies and Tracking Technologies">
          <Typography variant="body1">
            We use cookies, web beacons, and similar technologies to remember preferences, authenticate sessions, and analyze traffic. You may disable cookies through your browser settings; however, this may limit certain Platform features.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="childrens-privacy" title="9. Children's Privacy">
          <Typography variant="body1">
            The Platform is not intended for individuals under <strong>18 years of age</strong>. We do not knowingly collect Personal Data from minors. If we become aware that a minor's data has been collected without verifiable parental/guardian consent, we will delete it promptly.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="grievance-officer" title="10. Grievance Officer">
          <Typography variant="body1" paragraph>
            In accordance with the Information Technology Act, 2000 and rules made thereunder, and the DPDP Act, 2023, the details of the Grievance Officer/Data Protection contact are:
          </Typography>
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            <strong>Designation:</strong> Grievance Officer<br />
            <strong>Email:</strong>{' '}<Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link><br />
            <strong>Address:</strong> 6-3-240/6/1, 1st Floor, Sarada Street, Road D-block, Maheswari Towers, Prem Nagar Road adj. Lane, Banjara Hills, Hyderabad, Telangana 500004, India<br />
            <strong>Response Timeline:</strong> Acknowledgement within 24 hours; resolution within 15 days (or as prescribed by applicable law).
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            You may also lodge a complaint with the <strong>Data Protection Board of India</strong> if you are not satisfied with the resolution provided.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="cross-border" title="11. Cross-Border Data Transfer">
          <Typography variant="body1">
            Your Personal Data may be stored or processed on servers located outside India by our cloud service providers. We ensure such transfers comply with the DPDP Act and any restrictions notified by the Central Government from time to time.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="third-party-links" title="12. Third-Party Links">
          <Typography variant="body1">
            The Platform may contain links to third-party websites/social media platforms. We are not responsible for the privacy practices of such third parties, and we encourage you to review their respective privacy policies.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="changes" title="13. Changes to this Policy">
          <Typography variant="body1">
            We may update this Policy periodically to reflect changes in law or our practices. The updated version will be posted on this page with a revised "Last Updated" date. Continued use of the Platform after such changes constitutes acceptance of the revised Policy.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="governing-law" title="14. Governing Law and Jurisdiction">
          <Typography variant="body1">
            This Policy shall be governed by the laws of India. Courts at <strong>Hyderabad, Telangana</strong> shall have exclusive jurisdiction over any disputes arising from this Policy.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="contact" title="15. Contact Us">
          <Typography variant="body1" paragraph>For any questions regarding this Privacy Policy, please contact:</Typography>
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            <strong>Creativecrewz</strong><br />
            <strong>Email:</strong>{' '}<Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link><br />
            <strong>Address:</strong> 6-3-240/6/1, 1st Floor, Sarada Street, Road D-block, Maheswari Towers, Prem Nagar Road adj. Lane, Banjara Hills, Hyderabad, Telangana 500004, India
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* Related policies */}
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Related Policies</Typography>
          <Link component={RouterLink} to="/terms" color="primary" display="block" sx={{ mb: 0.5 }}>
            Terms and Conditions
          </Link>
          <Link component={RouterLink} to="/refund-policy" color="primary" display="block">
            Refund and Cancellation Policy
          </Link>
        </Box>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
